import { isMockRequest, resolveMockRequest } from '@/debug/debugMode';
import { useCallback } from 'react';

export class FetchError extends Error {
  public type: string;
  public title: string;
  public status: number;
  public errors: Record<string, string[]> | undefined;
  public traceId: string | undefined;

  constructor(res: Response, error: BackendError | undefined) {
    super();
    this.type = error?.type || 'Error';
    this.title = error?.title || res.statusText;
    this.status = error?.status || res.status;
    this.errors = error?.errors;
    this.traceId = error?.traceId;

    Object.setPrototypeOf(this, FetchError.prototype);
  }
}

type BackendError = {
  type: string;
  status: number;
  title: string;
  errors?: Record<string, string[]>;
  traceId?: string;
};
interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: BodyInit | Record<string, unknown> | null;
}

export type ApiFetch = <T = unknown>(endpoint: string, options?: ApiOptions) => Promise<T>;

export const useApi = () => {
  const apiFetch = useCallback(async <T = unknown,>(endpoint: string, options: ApiOptions = {}): Promise<T> => {
    const method = options.method || 'GET';

    if (import.meta.env.DEV && isMockRequest(endpoint, options.body)) {
      return (await resolveMockRequest(endpoint, method, options.body)) as T;
    }

    const headers = new Headers(options.headers || {});
    headers.append('Content-Type', 'application/json');

    const fetchOptions: RequestInit = {
      method,
      headers,
      body: method !== 'GET' ? (options.body ? JSON.stringify(options.body) : null) : undefined
    };

    const response = await fetch(endpoint, fetchOptions);

    if (!response.ok && response.status !== 204) {
      const errorText = await response.text();

      let errorData: BackendError | undefined;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = undefined;
      }

      const error = new FetchError(response, errorData);
      throw error;
    }

    if (response.status === 204 || !response.body) {
      return {} as T;
    }

    const contentType = response.headers.get('Content-Type');
    let responseData: any;

    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else if (contentType && contentType.includes('text/')) {
      responseData = await response.text();
    } else {
      responseData = await response.blob();
    }

    return responseData as T;
  }, []);

  return apiFetch;
};
